import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import type { ScreeningColumn } from '@app/src/pages/Findings/findingsUtils'
import { getColumnFindingsCount, getScreeningColumns, isColumnScreened } from '@app/src/pages/Findings/findingsUtils'
import type { ProviderFindings } from '@app/src/pages/Findings/Suppliers/FindingsSuppliersScene'
import { useScreeningTemplates } from '@app/src/pages/Findings/useFindingsView'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { useMemo } from 'react'

// Greenwashing isn't part of the Sustainability Profile's integrity screening — it's excluded from
// the cards, the finding counts, and the findings list below.
export const PROFILE_INTEGRITY_SCREENING_QUESTIONNAIRES: string[] = ['adverse-media', 'sanctions', 'pep']

export interface ProviderScreeningResult {
  column: ScreeningColumn
  count: number
  isScreened: boolean
}

interface UseProviderFindingsResult {
  screenings: ProviderScreeningResult[]
  totalFindings?: number
  isLoading: boolean
}

export function useProviderFindings(providerId: number): UseProviderFindingsResult {
  const { screeningTemplates, isLoading: templatesLoading } = useScreeningTemplates()

  const { items, isLoading: providersLoading } = useFetchCollectionWithPost<ProviderFindings>({
    endpoint: endpoints.findingsProviders,
    key: [FetchKey.FindingsProviders, 'profile', providerId],
    payload: {
      filter: [
        { name: 'provider.id', filters: [{ operator: Operators.EqualTo, value: providerId }] },
        { name: 'findingsState', filters: [{ operator: Operators.EqualTo, value: 'all' }] },
      ],
      include: [],
      pagination: { pageNumber: 1, itemsPerPage: 1 },
    },
    options: { enabled: Boolean(providerId) },
  })

  const row = items[0]

  const screenings = useMemo<ProviderScreeningResult[]>(
    () =>
      getScreeningColumns(screeningTemplates)
        .filter(column => PROFILE_INTEGRITY_SCREENING_QUESTIONNAIRES.includes(column.type))
        .map(column => ({
          column,
          count: getColumnFindingsCount(row?.countsByTemplateId, column),
          isScreened: isColumnScreened(row?.screenedTemplateIds, column),
        })),
    [screeningTemplates, row],
  )

  const totalFindings = useMemo(
    () =>
      screenings.some(screening => screening.isScreened) ? screenings.reduce((sum, s) => sum + s.count, 0) : undefined,
    [screenings],
  )

  return { screenings, totalFindings, isLoading: templatesLoading || providersLoading }
}
